import type { AssistantCitationGranularity } from "./assistant";
import type { SocialCitationPost, SocialPlatform } from "./social-listening";

/**
 * Knowledge Base (Decision 12 / S1) — single source of truth for context-material
 * upload ceilings. Drives backend multer limits
 * (`apps/backend/src/storages/audience-context-policy.ts`), the frontend
 * client-side guard, and UI labels. Never hardcode these elsewhere.
 *
 * Provider File Search allows 100MB per document. Per-storage file count is a
 * product/UI ceiling (not a provider hard limit); S2 sharding owns 5,000-doc orgs.
 */
export const MAX_CONTEXT_MATERIAL_FILE_MB = 100;
export const MAX_CONTEXT_MATERIAL_FILE_BYTES =
  MAX_CONTEXT_MATERIAL_FILE_MB * 1024 * 1024;

/** Max files per context / knowledge storage (bulk folder upload target). */
export const MAX_CONTEXT_MATERIAL_FILE_COUNT = 100;

/**
 * Staged per-file indexing progress (F1 / S1).
 *
 * Canonical write values: uploading → extracting → understanding → ready | failed.
 * Legacy read values (`pending`, `active`) remain valid forever for rows written
 * before this rename. `null` means historically searchable — treat as ready and
 * **never backfill** null rows or working stores lose grounding.
 *
 * `understanding` is reserved for the S3 understanding pass; S1 ingest writes
 * uploading/extracting/ready/failed only.
 */
export const CONTEXT_MATERIAL_INDEXING_STATUS = {
  UPLOADING: "uploading",
  EXTRACTING: "extracting",
  /**
   * Text and page structure are persisted; the provider index is still being
   * built. The document is already findable in Postgres library search from
   * this state onward.
   */
  INDEXING: "indexing",
  UNDERSTANDING: "understanding",
  READY: "ready",
  FAILED: "failed",
  /** @deprecated Prefer EXTRACTING for new writes */
  PENDING: "pending",
  /** @deprecated Prefer READY for new writes */
  ACTIVE: "active",
} as const;

export type ContextMaterialIndexingStatus =
  (typeof CONTEXT_MATERIAL_INDEXING_STATUS)[keyof typeof CONTEXT_MATERIAL_INDEXING_STATUS];

const READY_STATUSES = new Set<string>([
  CONTEXT_MATERIAL_INDEXING_STATUS.READY,
  CONTEXT_MATERIAL_INDEXING_STATUS.ACTIVE,
]);

const IN_PROGRESS_STATUSES = new Set<string>([
  CONTEXT_MATERIAL_INDEXING_STATUS.UPLOADING,
  CONTEXT_MATERIAL_INDEXING_STATUS.EXTRACTING,
  CONTEXT_MATERIAL_INDEXING_STATUS.INDEXING,
  CONTEXT_MATERIAL_INDEXING_STATUS.UNDERSTANDING,
  CONTEXT_MATERIAL_INDEXING_STATUS.PENDING,
]);

/**
 * States in which extraction has already persisted page spans and search text,
 * so Postgres library search can return the document even though the provider
 * index (and enrichment) may still be running.
 */
const SEARCHABLE_STATUSES = new Set<string>([
  CONTEXT_MATERIAL_INDEXING_STATUS.INDEXING,
  CONTEXT_MATERIAL_INDEXING_STATUS.UNDERSTANDING,
  CONTEXT_MATERIAL_INDEXING_STATUS.READY,
  CONTEXT_MATERIAL_INDEXING_STATUS.ACTIVE,
]);

/** Searchable / grounded. NULL is ready (legacy); never treat null as pending. */
export function isContextMaterialIndexingReady(
  status: string | null | undefined,
): boolean {
  if (status == null) {
    return true;
  }
  return READY_STATUSES.has(status);
}

/** Still in the ingest pipeline (not failed, not ready). */
export function isContextMaterialIndexingInProgress(
  status: string | null | undefined,
): boolean {
  if (status == null) {
    return false;
  }
  return IN_PROGRESS_STATUSES.has(status);
}

export function isContextMaterialIndexingFailed(
  status: string | null | undefined,
): boolean {
  return status === CONTEXT_MATERIAL_INDEXING_STATUS.FAILED;
}

/**
 * Findable in Postgres library search. Deliberately wider than
 * `isContextMaterialIndexingReady`: extraction-first ingest persists page spans
 * and search text before the provider index exists, and a document that failed
 * to index must stay searchable rather than disappear.
 */
export function isContextMaterialSearchable(
  status: string | null | undefined,
  extractionState?: string | null,
): boolean {
  if (extractionState === CONTEXT_MATERIAL_STAGE_STATE.OK) {
    return true;
  }
  if (status == null) {
    return true;
  }
  return SEARCHABLE_STATUSES.has(status);
}

/**
 * Per-stage ingest outcome. A single `indexingStatus` cannot express
 * "text was read, provider indexing failed", which is exactly the case where
 * the document must stay listed, searchable and downloadable.
 */
export const CONTEXT_MATERIAL_STAGE_STATE = {
  PENDING: "pending",
  OK: "ok",
  FAILED: "failed",
} as const;

export type ContextMaterialStageState =
  (typeof CONTEXT_MATERIAL_STAGE_STATE)[keyof typeof CONTEXT_MATERIAL_STAGE_STATE];

export const CONTEXT_MATERIAL_STAGE = {
  EXTRACTION: "extraction",
  INDEX: "index",
  UNDERSTANDING: "understanding",
} as const;

export type ContextMaterialStage =
  (typeof CONTEXT_MATERIAL_STAGE)[keyof typeof CONTEXT_MATERIAL_STAGE];

export interface ContextMaterialStageStates {
  extraction: ContextMaterialStageState;
  index: ContextMaterialStageState;
  understanding: ContextMaterialStageState;
}

/**
 * Roll per-stage outcomes up into the single row status the UI reads.
 *
 * Only a failed extraction produces `failed`: without text there is no
 * document to show. A failed provider index or a failed understanding pass
 * settle to `ready` because search, preview and download all still work — the
 * per-stage state is what surfaces the retry affordance.
 */
export function rollUpContextMaterialStages(
  stages: ContextMaterialStageStates,
): ContextMaterialIndexingStatus {
  if (stages.extraction === CONTEXT_MATERIAL_STAGE_STATE.FAILED) {
    return CONTEXT_MATERIAL_INDEXING_STATUS.FAILED;
  }
  if (stages.extraction === CONTEXT_MATERIAL_STAGE_STATE.PENDING) {
    return CONTEXT_MATERIAL_INDEXING_STATUS.EXTRACTING;
  }
  if (stages.index === CONTEXT_MATERIAL_STAGE_STATE.PENDING) {
    return CONTEXT_MATERIAL_INDEXING_STATUS.INDEXING;
  }
  if (stages.understanding === CONTEXT_MATERIAL_STAGE_STATE.PENDING) {
    return CONTEXT_MATERIAL_INDEXING_STATUS.UNDERSTANDING;
  }
  return CONTEXT_MATERIAL_INDEXING_STATUS.READY;
}

/**
 * How long a document may sit un-started before the UI is allowed to call it
 * stalled. Long enough that a busy worker pool or a large upload backlog does
 * not produce false alarms, short enough that a user does not stare at a
 * spinner for an afternoon.
 */
export const CONTEXT_MATERIAL_STALL_GRACE_MS = 15 * 60 * 1000;

/**
 * Row statuses that mean "no worker has picked this document up yet".
 * `extracting` is excluded on purpose: it is written by the extraction worker
 * itself, so seeing it proves processing started.
 */
const NEVER_STARTED_STATUSES = new Set<string>([
  CONTEXT_MATERIAL_INDEXING_STATUS.UPLOADING,
  CONTEXT_MATERIAL_INDEXING_STATUS.PENDING,
]);

export interface ContextMaterialStalledInput {
  indexingStatus: string | null | undefined;
  extractionState: string | null | undefined;
  createdAt: string | Date;
  /** Defaults to wall clock; injected in tests and for deterministic reads. */
  now?: Date | number;
}

/**
 * True when a document never entered the ingest pipeline at all.
 *
 * Derived on read rather than swept by a cron job: "stuck" is a pure function
 * of three columns we already load for every list row, so a scheduled scanner
 * would add a table, a lock and a lag window to compute an answer the read
 * path can produce for free. It also stays correct after a deploy, a Redis
 * flush or a queue rename — none of which a persisted `stalled` flag would
 * survive. The cost is that nothing is repaired automatically; the UI turns
 * this into a Retry affordance and the user decides.
 */
export function isContextMaterialStalled(
  input: ContextMaterialStalledInput,
): boolean {
  if (
    input.indexingStatus == null ||
    !NEVER_STARTED_STATUSES.has(input.indexingStatus)
  ) {
    return false;
  }
  if (
    input.extractionState != null &&
    input.extractionState !== CONTEXT_MATERIAL_STAGE_STATE.PENDING
  ) {
    return false;
  }
  const createdAtMs =
    input.createdAt instanceof Date
      ? input.createdAt.getTime()
      : Date.parse(input.createdAt);
  if (!Number.isFinite(createdAtMs)) {
    return false;
  }
  const nowMs =
    input.now == null
      ? Date.now()
      : input.now instanceof Date
        ? input.now.getTime()
        : input.now;
  return nowMs - createdAtMs >= CONTEXT_MATERIAL_STALL_GRACE_MS;
}

/**
 * Coerce a DB row status for API responses.
 * - Known statuses pass through (including legacy pending/active).
 * - NULL → ready (display only; does not write the DB).
 * - Unknown + geminiFileName → ready; else extracting.
 */
export function coerceContextMaterialIndexingStatus(
  status: string | null | undefined,
  hasGeminiFileName: boolean,
): ContextMaterialIndexingStatus {
  if (status == null) {
    return CONTEXT_MATERIAL_INDEXING_STATUS.READY;
  }
  if (
    READY_STATUSES.has(status) ||
    IN_PROGRESS_STATUSES.has(status) ||
    status === CONTEXT_MATERIAL_INDEXING_STATUS.FAILED
  ) {
    return status as ContextMaterialIndexingStatus;
  }
  return hasGeminiFileName
    ? CONTEXT_MATERIAL_INDEXING_STATUS.READY
    : CONTEXT_MATERIAL_INDEXING_STATUS.EXTRACTING;
}

/**
 * Estimate wall-clock time for a synthetic-org back-fill under the per-org
 * in-flight fairness cap. Pure — no I/O. Used by the S1 synthetic org generator
 * to answer "how long to rebuild a 5,000-document org" without hitting Gemini.
 */
export interface BackfillEstimateInput {
  documentCount: number;
  /** Mean provider import duration per document (seconds). */
  avgImportSeconds: number;
  /** Concurrent imports allowed per org (fairness cap). */
  orgMaxInFlight: number;
}

export interface BackfillEstimate {
  documentCount: number;
  avgImportSeconds: number;
  orgMaxInFlight: number;
  batches: number;
  wallClockSeconds: number;
  wallClockMinutes: number;
  wallClockHours: number;
  meetsSearchableWithinFiveMinutesPerFile: boolean;
}

export function estimateContextMaterialBackfill(
  input: BackfillEstimateInput,
): BackfillEstimate {
  const documentCount = Math.max(0, Math.floor(input.documentCount));
  const avgImportSeconds = Math.max(0, input.avgImportSeconds);
  const orgMaxInFlight = Math.max(1, Math.floor(input.orgMaxInFlight));
  const batches =
    documentCount === 0 ? 0 : Math.ceil(documentCount / orgMaxInFlight);
  const wallClockSeconds = batches * avgImportSeconds;
  return {
    documentCount,
    avgImportSeconds,
    orgMaxInFlight,
    batches,
    wallClockSeconds,
    wallClockMinutes: wallClockSeconds / 60,
    wallClockHours: wallClockSeconds / 3600,
    meetsSearchableWithinFiveMinutesPerFile: avgImportSeconds <= 300,
  };
}

/** Hub document visibility values stamped into Gemini metadata. */
export const KNOWLEDGE_DOCUMENT_VISIBILITY = {
  ORG: "org",
  RESTRICTED: "restricted",
  PRIVATE: "private",
} as const;

export type KnowledgeDocumentVisibility =
  (typeof KNOWLEDGE_DOCUMENT_VISIBILITY)[keyof typeof KNOWLEDGE_DOCUMENT_VISIBILITY];

export interface KnowledgeDocumentFacet {
  assignmentToken: string;
  kind: string;
  displayName: string;
  value: string;
  provenance: "auto" | "human" | "connector";
  confirmed: boolean;
  /** Soft-rejected inferred facets stay out of active chips. */
  rejected?: boolean;
  /** 0–1 when provenance=auto; omit/null for human. */
  confidence?: number | null;
  taxonomyNodeToken?: string | null;
}

export interface KnowledgeDocumentUsageItem {
  studyId: string;
  populationId: number;
  activatedAt: string;
}

export interface KnowledgeDocumentVersionItem {
  label: string;
  version: string;
  createdAt: string;
  isCurrent: boolean;
  /** Uploader display name when known. */
  ownerName?: string | null;
  /** upload | replace | connector_sync | simulation_publish */
  origin?: string | null;
}

export interface KnowledgeDocumentComment {
  token: string;
  body: string;
  /** Null for document-level comments and for documents without a page rail. */
  pageNumber: number | null;
  createdAt: string;
  /** Null when the author's account was removed. */
  authorToken: string | null;
  /** Display name, falling back to the author's email when no name is set. */
  authorName: string | null;
}

export interface KnowledgeDocumentSocialInfo {
  trackerToken: string;
  trackerName: string;
  platform: SocialPlatform;
  windowStart: string;
  windowEnd: string;
  itemCount: number;
  /** Public token of the digest's Topic, for linking to its page. Null when the Topic is gone. */
  topicToken: string | null;
}

export interface KnowledgeDocumentListItem {
  token: string;
  originalFilename: string | null;
  visibility: KnowledgeDocumentVisibility;
  indexingStatus: ContextMaterialIndexingStatus;
  indexingError?: string | null;
  /** Per-stage ingest outcomes so each failure retries on its own. */
  extractionState?: ContextMaterialStageState;
  extractionError?: string | null;
  indexState?: ContextMaterialStageState;
  indexError?: string | null;
  understandingState?: ContextMaterialStageState;
  understandingError?: string | null;
  /** True once page spans + search text exist, regardless of provider index. */
  searchable?: boolean;
  /**
   * Derived: the document was accepted but no worker ever picked it up.
   * See `isContextMaterialStalled`.
   */
  stalled?: boolean;
  fileSize: number | null;
  mimeType: string | null;
  storageId: number | null;
  createdAt: string;
  deletedAt?: string | null;
  /** S3 understanding */
  title?: string | null;
  /**
   * True once a human renames the document via `PATCH /documents/:token`.
   * `updateDocumentUnderstanding` skips `title` on the next re-index while
   * this is set, so a re-index never silently clobbers the rename.
   */
  titleSetByUser?: boolean;
  summary?: string | null;
  /**
   * True once a human edits the description via `PATCH /documents/:token`.
   * `updateDocumentUnderstanding` skips `summary` on the next re-index while
   * this is set, so a re-index never silently clobbers the edit.
   */
  summarySetByUser?: boolean;
  excerpt?: string | null;
  findingsMarkdown?: string | null;
  methodologyMarkdown?: string | null;
  understandingVersion?: string | null;
  understoodAt?: string | null;
  sourceUrl?: string | null;
  linkTitle?: string | null;
  linkDescription?: string | null;
  linkThumbnailUrl?: string | null;
  /** Persisted first-page / cover preview URL for library cards. */
  coverImageUrl?: string | null;
  facets?: Array<KnowledgeDocumentFacet>;
  /** F14 — study write-back simulation artifact. */
  isSimulationArtifact?: boolean;
  sourceStudyId?: string | null;
  /** Scanned/image-only PDF with little extractable text. */
  isScannedPdf?: boolean;
  /**
   * Total page count, persisted from segmentation's page spans. Null until
   * segmentation has run at least once. Powers "Pages: N" in the Details
   * panel without an on-demand call.
   */
  pageCount?: number | null;
  /** Displayed + searchable author; not a filter. */
  author?: string | null;
  /** Deterministic owner (uploader). Filterable; no confirm control. */
  ownerName?: string | null;
  ownerToken?: string | null;
  /** Per-document retrieval instructions (Hub edit surface). */
  instructions?: string | null;
  /** Present when the document is a social-listening digest. */
  social?: KnowledgeDocumentSocialInfo | null;
  /** Studies that activated this document. Present on getDocument. */
  usedIn?: Array<KnowledgeDocumentUsageItem>;
  /** File/replace history (current + superseded versions). */
  versions?: Array<KnowledgeDocumentVersionItem>;
}

export interface KnowledgeAskCitation {
  documentName: string;
  documentToken?: string | null;
  snippet: string;
  granularity: AssistantCitationGranularity;
  extractionVersion: string;
  /** Present when granularity is `post` (social listening digest). */
  post?: SocialCitationPost | null;
  pageNumber?: number | null;
  /**
   * Unicode character offsets into the final, trimmed ANSWER text, for inline
   * citation markers. Emitted only when the grounding support could be resolved
   * against that text; absent means the client anchors from the snippet rather
   * than trusting a number that may not address the answer at all.
   */
  charStart?: number | null;
  charEnd?: number | null;
  /**
   * Offsets into the SOURCE page's text, resolved server-side. When present the
   * viewer highlights this exact range; when null it must highlight nothing
   * rather than fuzzy match, which mis-marks repeated phrasing.
   */
  unitCharStart?: number | null;
  unitCharEnd?: number | null;
  anchorConfidence?: number | null;
  pageUnknown?: boolean;
  citationToken?: string | null;
  /** Video chapter time (mm:ss) when granularity is timestamp. */
  timestampLabel?: string | null;
}

export interface KnowledgeAskResponse {
  askId: string;
  text: string;
  citations: Array<KnowledgeAskCitation>;
  /** Versioned ask contract (S4). Clients should ignore unknown fields. */
  schemaVersion?: string;
  refused?: boolean;
  gaps?: Array<string>;
}

export interface KnowledgeSearchPageHit {
  unitKind: string;
  unitIndex: number;
  label?: string | null;
  snippet: string;
  score?: number;
  charStart?: number | null;
  charEnd?: number | null;
}

export interface KnowledgeSearchHit {
  token: string;
  originalFilename: string | null;
  summary: string | null;
  score: number;
  /** Nested page/slide/sheet matches under this document; omit or [] when none. */
  pages?: Array<KnowledgeSearchPageHit>;
  pageHitCount?: number;
}

export interface KnowledgeSearchResponse {
  query: string;
  hits: Array<KnowledgeSearchHit>;
  suggestions: Array<{
    kind: "document" | "query";
    text: string;
    documentToken?: string;
  }>;
}

export interface KnowledgeFacetAggregateValue {
  value: string;
  count: number;
  taxonomyNodeToken?: string | null;
  /**
   * Facet value tokens behind this value text, sorted (one per source value
   * with the same text). Library collection rules and the resources filters
   * take these tokens, never the text.
   */
  tokens?: Array<string>;
}

export interface KnowledgeFacetAggregate {
  kind: string;
  displayName: string;
  values: Array<KnowledgeFacetAggregateValue>;
  /** Documents in the org with no assignment for this kind. */
  unknownCount: number;
}

/** Back office: provider spend recorded against one library document (today, indexing only). */
export interface KnowledgeDocumentCost {
  documentToken: string;
  tokens: number;
  estimatedCents: number;
}

export interface KnowledgeFacetsResponse {
  facets: Array<KnowledgeFacetAggregate>;
  documentCount: number;
}

export interface KnowledgeBulkUploadResponse {
  batchId: string;
  documents: Array<KnowledgeDocumentListItem>;
}

export type KnowledgeLinkIngestResultStatus =
  | 'queued'
  | 'duplicate'
  | 'rejected';

export interface KnowledgeLinkIngestResultItem {
  url: string;
  status: KnowledgeLinkIngestResultStatus;
  /** Present when status is `duplicate` or `queued` (placeholder card). */
  documentToken?: string;
  /** Present when status is `rejected`. */
  failureReason?: string;
}

export interface KnowledgeLinkIngestResponse {
  batchId: string;
  results: Array<KnowledgeLinkIngestResultItem>;
}

/**
 * Socket.IO event `knowledge:document-indexing-updated` on the org room.
 * Emitted whenever `files.indexing_status` changes (Gemini import, understanding, failure).
 * Clients already join `organization:{token}` on connect — no extra subscribe needed.
 */
export interface KnowledgeDocumentIndexingUpdatedEvent {
  organizationToken: string;
  /** `files.token` — matches ContextMaterialFileItem.id on study/audience surfaces */
  fileToken: string;
  /** Hub document token when a `documents` row exists; null for context-only files */
  documentToken: string | null;
  /**
   * Population (audience) token when the file belongs to an audience-context or
   * quick-study-knowledge storage. Null for org-wide Hub documents.
   */
  populationToken?: string | null;
  originalFilename: string | null;
  indexingStatus: ContextMaterialIndexingStatus | string;
  indexingError: string | null;
  /** When set, Hub cards can swap placeholder for the persisted cover without refetch. */
  coverImageUrl?: string | null;
  /** Library card fields — patched in when understanding (or link capture) finishes. */
  title?: string | null;
  summary?: string | null;
  excerpt?: string | null;
  timestamp: string;
}

/**
 * Socket.IO event `knowledge:link-ingest-updated` on the org room.
 * Fired when a Hub link capture finishes: document created (import queued) or terminal failure.
 */
export interface KnowledgeLinkIngestUpdatedEvent {
  organizationToken: string;
  batchId: string;
  url: string;
  status: 'created' | 'failed';
  documentToken?: string | null;
  failureReason?: string | null;
  timestamp: string;
}

